import { useTranslations } from "use-intl";

import { useFeaturePageCatalog } from "@/features/feature-page/feature-page-catalog-context";
import { FeaturePageCitations } from "@/features/feature-page/feature-page-citations";
import { FeaturePageSection } from "@/features/feature-page/feature-page-section";
import { FeaturePageVisual } from "@/features/feature-page/feature-page-visual";
import { getFactValueScale } from "@/features/feature-page/getFactValueScale";
import { cn } from "@/lib/utils";
import type { ResolvedFeaturePageBlock } from "@garimpa/api/featurePages/types";

const valueScaleClasses = {
  short: "text-4xl leading-none",
  medium: "text-2xl leading-tight",
  long: "text-lg leading-snug",
} as const;

export function EntitySpotlightBlock({
  block,
}: {
  block: Extract<ResolvedFeaturePageBlock, { type: "entitySpotlight" }>;
}) {
  const t = useTranslations("globals.featurePages");
  const entity = useFeaturePageCatalog().getEntityByRef(block.catalogRefId);
  if (!entity) return null;
  const EntityHeading = block.heading ? "h3" : "h2";
  return (
    <FeaturePageSection
      block={block}
      resolvedBackground={block.resolvedBackground}
      labelledById={`${block.id}-heading`}
    >
      <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
        {block.resolvedVisual ? (
          <a
            href={entity.href}
            className={cn(
              "group overflow-hidden rounded-2xl border border-border bg-secondary shadow-xl transition hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              block.imageSide === "right" && "lg:order-2",
            )}
          >
            <FeaturePageVisual
              visual={block.resolvedVisual}
              className="aspect-square h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"
            />
          </a>
        ) : null}
        <div>
          {block.heading ? (
            <h2
              id={`${block.id}-heading`}
              className="text-center text-3xl font-bold tracking-tight sm:text-5xl"
            >
              {block.heading}
            </h2>
          ) : null}
          <EntityHeading
            id={block.heading ? undefined : `${block.id}-heading`}
            className="mt-4 text-2xl font-semibold text-primary"
          >
            {block.title ?? entity.title}
          </EntityHeading>
          <p className="mt-5 text-lg leading-8 text-muted-foreground">{block.body}</p>
          {block.facts?.length ? (
            <dl className="mt-8 grid gap-4 sm:grid-cols-2">
              {block.facts.map((fact) => {
                const valueScale = getFactValueScale(fact.value);

                return (
                  <div
                    key={fact.label}
                    className="flex h-full flex-col rounded-xl border border-border bg-card/80 p-4 backdrop-blur"
                  >
                    <dt className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      {fact.label}
                    </dt>
                    <dd
                      className={cn(
                        "mt-2 text-balance font-bold tracking-[-0.02em]",
                        valueScaleClasses[valueScale],
                        fact.sourceIds?.length && "mb-5",
                      )}
                    >
                      {fact.value}
                    </dd>
                    <FeaturePageCitations sourceIds={fact.sourceIds} placement="footer" />
                  </div>
                );
              })}
            </dl>
          ) : null}
          <a
            href={entity.href}
            className="mt-7 inline-flex items-center gap-2 rounded-sm font-bold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t("viewCatalogEntity", {
              entityType: entity.entityType,
              name: entity.title,
            })}{" "}
            <span aria-hidden="true">→</span>
          </a>
        </div>
      </div>
    </FeaturePageSection>
  );
}
